import React, { useEffect, useState } from 'react';
import { Form, FormInstance, TextInput } from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';

import { CardBlock } from '../../card';
import { LoadingBlock } from '../../loading/progress';
import Modal from '../../modal';

import Button from '../../../molecules/button';
import Icon from '../../../atoms/icon';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { ConfigComponent } from '../../../../libs/apps';
import { PostJSON, BaseResponse } from '../../../../libs/request';
import { useGetSettings } from '../../../../libs/settings';

interface EpxPreDiabetesConfigFormProps {
  outreachPhone: string;
}

interface OutreachPhoneResponse extends BaseResponse {
  outreachPhone: string;
}

const REGEX_PHONE = /^\D*(\d\D*){10}$/;

/**
 * Pre-Diabetes Config
 *
 * For setting the outreach phone number
 *
 * @returns JSX.Element
 */
export const EpxPreDiabetesConfigForm: ConfigComponent = () => {
  const [outreachPhone, setOutreachPhone] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const settings = useGetSettings({ appKey: 'epxprediabetes' }).settings;
  const form = new FormInstance<EpxPreDiabetesConfigFormProps>({
    outreachPhone: settings?.outreachPhone ?? ''
  });

  useEffect(() => {
    if (form && settings) {
      const values = JSON.parse(settings.values);
      form.setData(values);
    }
  }, [form, settings]);

  const saveOutreachPhone = (data: EpxPreDiabetesConfigFormProps) => {
    setIsUpdating(true);
    PostJSON<OutreachPhoneResponse>({
      url: '/settings/edit/',
      params: {
        app: 'epxprediabetes',
        settings: {
          outreachPhone: data.outreachPhone
        }
      }
    }).then((response) => {
      setIsUpdating(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Saving Configuration',
          content: response.message
        });
        return;
      }
      Modal.success({
        title: 'Configuration Updated',
        content: 'The configuration has been updated successfully!'
      });
      const newOutreachPhone = (response as OutreachPhoneResponse)
        .outreachPhone;
      if (newOutreachPhone) {
        setOutreachPhone(newOutreachPhone);
      }
      return;
    });
  };

  // Outreach phone still loading
  if (outreachPhone === null) {
    return (
      <React.Fragment>
        <CardBlock>
          <LoadingBlock />
        </CardBlock>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <CardBlock>
        <Row>
          <Col sm={24}>
            <Form form={form} onSubmit={saveOutreachPhone}>
              <TextInput
                label="Outreach Phone Number"
                name="outreachPhone"
                placeholder="e.g. 5556667777"
                required
                validate={(value) => {
                  if (!value) {
                    return 'The outreach phone number is required!';
                  }
                  if (!REGEX_PHONE.test(value)) {
                    return 'The outreach phone number must only be 10 digits!';
                  }
                  return null;
                }}
              />
            </Form>
          </Col>
        </Row>
        <Row>
          <Col sm={8}>
            <Button
              intent="primary"
              icon={<Icon icon={faSave}></Icon>}
              loading={isUpdating}
              onClick={() => form.submit()}
            >
              Save Configuration
            </Button>
          </Col>
        </Row>
      </CardBlock>
    </React.Fragment>
  );
};
